import * as Dialog from '@radix-ui/react-dialog';
import { X, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LanguageModalContent } from '@/types/content';

export interface LanguageModalProps {
  content?: LanguageModalContent;
  isOpen: boolean;
  onClose: () => void;
  selectedLanguage?: string;
  onLanguageChange?: (languageId: string) => void;
  className?: string;
}

export function LanguageModal({
  content,
  isOpen,
  onClose,
  selectedLanguage = 'en',
  onLanguageChange,
  className
}: LanguageModalProps) {
  if (!content) {
    return null;
  }

  const handleLanguageSelect = (languageId: string) => {
    onLanguageChange?.(languageId);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed top-[30.43px] left-0 right-0 bottom-0 z-[130] bg-black/50 animate-fade-in"
          data-testid="language-modal-overlay"
        />
        <Dialog.Content
          className={cn(
            "fixed inset-0 z-[131] flex items-center justify-center pointer-events-none",
            className
          )}
          aria-describedby={undefined}
        >
          <div
            className={cn(
              "w-[300px] bg-background rounded-xl shadow-lg p-6",
              "animate-scale-in pointer-events-auto",
              "focus:outline-none"
            )}
            data-testid="language-modal-content"
          >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-foreground">
              {content.title}
            </Dialog.Title>
            <Dialog.Close
              className="p-1 rounded-full hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={content.closeLabel || "Close"}
            >
              <X className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            </Dialog.Close>
          </div>

          {/* Language Options */}
          <div className="space-y-4">
            <fieldset>
              <legend className="sr-only">{content.title}</legend>
              {content.languages.map((language) => (
                <label
                  key={language.id}
                  className={cn(
                    "flex items-center gap-3 cursor-pointer py-2 px-3 -mx-3 rounded-lg",
                    "hover:bg-muted transition-colors"
                  )}
                >
                  <input
                    type="radio"
                    name="language"
                    value={language.id}
                    checked={selectedLanguage === language.id}
                    onChange={() => handleLanguageSelect(language.id)}
                    className="w-5 h-5 text-primary border-border accent-foreground focus:ring-primary focus:ring-2"
                  />
                  <span className="text-base text-foreground">
                    {language.label}
                  </span>
                </label>
              ))}
            </fieldset>

            {/* More Languages Button */}
            {content.moreLanguages && (
              <a
                href={content.moreLanguages.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-3 w-full px-4 py-3",
                  "border border-border rounded-md",
                  "hover:bg-muted transition-colors",
                  "text-base text-foreground",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                )}
              >
                <HelpCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                <span>{content.moreLanguages.label}</span>
              </a>
            )}
          </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

LanguageModal.displayName = 'LanguageModal';
